"use client";

import Link from "next/link";
import { useLocale, useTranslations } from "next-intl";
import { BookOpen, CheckCircle2, MessageSquareText, Play, Settings2, ShieldCheck } from "lucide-react";
import { useAuth } from "@/contexts/auth-context";
import { useWorkspace } from "@/contexts/workspace-context";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { HeroSection } from "@/components/home/hero-section";
import { ToolsGrid } from "@/components/home/tools-grid";

const actions = [
  { path: "/runs", icon: Play, capabilities: ["runs.create", "runs.view_own", "runs.view_all"], label: "runs" },
  { path: "/prompt-governance", icon: MessageSquareText, capabilities: ["prompts.view"], label: "prompts" },
  { path: "/config-governance", icon: Settings2, capabilities: ["configs.view"], label: "configs" },
  { path: "/content-safety", icon: ShieldCheck, capabilities: ["content_safety.read", "content_safety.manage"], label: "safety" },
  { path: "/feedback-curation", icon: BookOpen, capabilities: ["feedback.read", "feedback.curate"], label: "feedback" },
] as const;

export function HomeExperience() {
  const locale = useLocale();
  const t = useTranslations("homeWorkspace");
  const { isAuthenticated, isLoading, hasCapability } = useAuth();
  const { workspaces, selected, loading, error, refresh } = useWorkspace();

  if (isLoading) {
    return <Card><CardContent className="p-6"><p className="text-sm text-muted-foreground">{t("loading")}</p></CardContent></Card>;
  }
  if (!isAuthenticated) {
    return (
      <div className="space-y-8">
        <HeroSection />
        <ToolsGrid />
      </div>
    );
  }

  const availableActions = actions.filter((action) => action.capabilities.some(hasCapability));

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="space-y-1">
              <CardTitle className="text-2xl">{t("title")}</CardTitle>
              <CardDescription>{t("intro")}</CardDescription>
            </div>
            {selected && (
              <div className="flex items-center gap-2 rounded-full border border-border bg-muted px-3 py-1 text-sm">
                <CheckCircle2 className="h-4 w-4 text-primary" />
                {t("ready")}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading && <p className="text-sm text-muted-foreground">{t("loading")}</p>}
          {!loading && error && (
            <div className="space-y-3" role="alert">
              <p className="text-sm text-destructive">{t("unavailable")}</p>
              <Button variant="outline" onClick={refresh}>{t("refresh")}</Button>
            </div>
          )}
          {!loading && !error && workspaces.length === 0 && (
            <div className="rounded-lg border border-dashed border-border p-5">
              <h2 className="font-medium">{t("noWorkspaceTitle")}</h2>
              <p className="mt-2 text-sm text-muted-foreground">{t("noWorkspace")}</p>
            </div>
          )}
          {!loading && !error && workspaces.length > 1 && !selected && (
            <p className="text-sm text-muted-foreground">{t("chooseWorkspace")}</p>
          )}
          {selected && (
            <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div className="rounded-lg border border-border p-4">
                <dt className="text-sm text-muted-foreground">{t("organization")}</dt>
                <dd className="mt-1 font-medium">{selected.organization_name}</dd>
              </div>
              <div className="rounded-lg border border-border p-4">
                <dt className="text-sm text-muted-foreground">{t("tenant")}</dt>
                <dd className="mt-1 font-medium">{selected.tenant_name}</dd>
              </div>
              <div className="rounded-lg border border-border p-4">
                <dt className="text-sm text-muted-foreground">{t("domain")}</dt>
                <dd className="mt-1 font-medium">{selected.domain_name}</dd>
              </div>
            </dl>
          )}
        </CardContent>
      </Card>

      {selected && availableActions.length > 0 && (
        <section className="space-y-3" aria-labelledby="authorized-work-heading">
          <div>
            <h2 id="authorized-work-heading" className="text-xl font-semibold">{t("authorizedWork")}</h2>
            <p className="text-sm text-muted-foreground">{t("authorizedWorkHelp")}</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {availableActions.map((action) => {
              const Icon = action.icon;
              return (
                <Card key={action.path}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-3">
                      <div className="rounded-lg bg-muted p-2 text-primary">
                        <Icon className="h-5 w-5" />
                      </div>
                      <CardTitle className="text-base">{t(action.label)}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Button asChild variant="outline" className="w-full">
                      <Link href={`/${locale}${action.path}`}>{t("open")}</Link>
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}
